// Widget-board defaults — the SINGLE source of truth for every default board a
// widget view starts from. Imported by BOTH sides of the seeding contract:
//   - apps/api/core  — WidgetsService.listByView lazily seeds an uninitialized
//     view's board inside the first read (widget-view-defaults.ts routes a view
//     kind to one of these boards via managedDefaultSeeds/dashboardTemplateSeeds).
//   - apps/client/salestrak-web — the managed catalogs (my-work-widgets.ts,
//     team-overview-widgets.ts) and the template chooser
//     (settings/dashboard-templates.ts) wrap these entries with display copy.
// Because both import the same data, the persisted seeds cannot drift.
//
// PERSISTED IDENTITY — the values here outlive any one build:
//   - `widgetKey` matches a placed widget back to its catalog/template entry
//     (managed migration + idempotent seeding key on it). Never rename one.
//   - Template seed ORDER is identity (`<templateKey>:<index>` widgetKeys are
//     baked from array position). Never reorder an existing template's seeds —
//     append only.
//   - Managed layout versions gate the client's managed-layout migration; bump
//     the version const when a managed board's designed layout changes.
//
// The key unions (MyWorkWidgetKey, …) are the contract: adding a board entry
// means widening its union, which forces the client's presentation maps to
// cover it.

/**
 * One placed widget of a default board — the persisted seed shape (matches
 * the client's `WidgetSeed` and the core API's `WidgetViewSeed`).
 */
export interface WidgetBoardSeed {
  type: string;
  config: Record<string, unknown>;
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * A managed catalog entry: a seed plus its stable identity and whether it is
 * part of the board a first read materializes.
 */
export interface ManagedBoardWidget<K extends string = string> extends WidgetBoardSeed {
  widgetKey: K;
  defaultOn: boolean;
}

export type MyWorkWidgetKey =
  | "my-work"
  | "needs-attention"
  | "notepad"
  | "recently-added"
  | "overview.closed"
  | "activity"
  | "favorites";

export type TeamOverviewWidgetKey =
  | "team-overview.active"
  | "team-overview.members"
  | "team-overview.workload"
  | "team-overview.activity";

export type DashboardTemplateKey = "pipeline-overview" | "team-performance" | "projects-activity";

export interface DashboardTemplateBoard {
  key: DashboardTemplateKey;
  seeds: WidgetBoardSeed[];
}

/**
 * Age "bad" = At Risk / Off Track, persisted as an advanced-filter builder
 * state (assignable to the client's `FilterState`). The client compiles it at
 * read time via `resolveWidgetFilters`; the client's typed copy re-exports
 * this one (widget-queries.ts).
 */
export const BAD_AGE_QUERY: {
  logic: "AND";
  blocks: Array<{
    logic: "AND";
    filters: Array<{ field: string; operator: "in"; value: string[] }>;
  }>;
} = {
  logic: "AND",
  blocks: [
    {
      logic: "AND",
      filters: [{ field: "age.status", operator: "in", value: ["atRisk", "offTrack"] }],
    },
  ],
};

// ── member: My Work (managed catalog) ───────────────────────────────────────
// My Work is a curated personal home, not a reporting dashboard. Its 12-column
// storage grid deliberately uses only 6- and 12-column spans, producing a
// stable two-column rhythm of half- and full-width snapshots.

/** My Work's current managed-layout revision, baked into every placed widget. */
export const MY_WORK_LAYOUT_VERSION = 4;

export const MY_WORK_BOARD: ManagedBoardWidget<MyWorkWidgetKey>[] = [
  {
    widgetKey: "my-work",
    type: "entity-list",
    config: { sort: "updated", limit: 8, title: "My Work", home: true, showTitleCount: true },
    x: 0,
    y: 0,
    w: 12,
    h: 5,
    defaultOn: true,
  },
  {
    widgetKey: "needs-attention",
    type: "entity-list",
    config: {
      sort: "updated",
      limit: 6,
      title: "Needs attention",
      home: true,
      showTitleCount: true,
      filters: { query: BAD_AGE_QUERY },
    },
    x: 0,
    y: 5,
    w: 6,
    h: 4,
    defaultOn: true,
  },
  {
    widgetKey: "notepad",
    type: "note",
    config: { title: "Notepad" },
    x: 6,
    y: 5,
    w: 6,
    h: 4,
    defaultOn: true,
  },
  {
    widgetKey: "recently-added",
    type: "entity-list",
    config: { sort: "created", limit: 6, title: "Recently added", home: true, showTitleCount: true },
    x: 0,
    y: 9,
    w: 6,
    h: 4,
    defaultOn: true,
  },
  {
    widgetKey: "overview.closed",
    type: "projects-closed",
    config: { title: "Newly closed this week", filters: { dateRange: { key: "this-week" } } },
    x: 6,
    y: 9,
    w: 6,
    h: 4,
    defaultOn: true,
  },
  {
    widgetKey: "activity",
    type: "activity-list",
    config: { limit: 20, title: "Your recent activity" },
    x: 0,
    y: 13,
    w: 12,
    h: 5,
    defaultOn: true,
  },
  {
    widgetKey: "favorites",
    type: "favorites-list",
    config: { sort: "updated", limit: 6, title: "Favorites", home: true },
    x: 0,
    y: 18,
    w: 6,
    h: 4,
    defaultOn: false,
  },
];

// ── team `-overview`: Team Overview (managed catalog) ───────────────────────
// Team Overview is NOT a free-form surface: the user's only control is which
// of THESE widgets are visible, and the designed positions ARE the layout.

/** Team Overview's current managed-layout revision. */
export const TEAM_OVERVIEW_LAYOUT_VERSION = 2;

export const TEAM_OVERVIEW_BOARD: ManagedBoardWidget<TeamOverviewWidgetKey>[] = [
  // Team facts — count + roster in the left rail.
  {
    widgetKey: "team-overview.active",
    type: "metric",
    config: { measure: "total", dateField: "lastUpdateDate", title: "Active projects" },
    x: 0,
    y: 0,
    w: 4,
    h: 4,
    defaultOn: true,
  },
  {
    widgetKey: "team-overview.members",
    type: "team-members",
    config: { title: "Members" },
    x: 0,
    y: 4,
    w: 12,
    h: 6,
    defaultOn: true,
  },
  // Workload + activity — wide context panels to the right.
  {
    widgetKey: "team-overview.workload",
    type: "dimension-bar",
    // Dimension-grouped widgets bake NO title: the module's `dynamicTitle`
    // derives it from `groupBy` ("Workload by assignee") so the chrome tracks
    // regrouping. Only a user's explicit rename writes `config.title`.
    config: { groupBy: "assignee", sort: "volume", dateField: "lastUpdateDate" },
    x: 4,
    y: 0,
    w: 8,
    h: 4,
    defaultOn: true,
  },
  {
    widgetKey: "team-overview.activity",
    type: "activity-list",
    config: { limit: 30, title: "Recent activity" },
    x: 0,
    y: 10,
    w: 12,
    h: 4,
    defaultOn: true,
  },
];

// ── dashboard templates (team / workflow Dashboard tabs) ────────────────────
// The starting boards the template chooser and Start-over dialog offer, and
// the source of the server's team/workflow first-read defaults.

/** Bake `<templateKey>:<index>` widgetKeys into a template's seeds. */
function withTemplateKeys(key: DashboardTemplateKey, seeds: WidgetBoardSeed[]): WidgetBoardSeed[] {
  return seeds.map((seed, index) => ({
    ...seed,
    config: { ...seed.config, widgetKey: `${key}:${index}` },
  }));
}

function templateBoard(key: DashboardTemplateKey, seeds: WidgetBoardSeed[]): DashboardTemplateBoard {
  return { key, seeds: withTemplateKeys(key, seeds) };
}

export const DASHBOARD_TEMPLATE_BOARDS: DashboardTemplateBoard[] = [
  templateBoard("pipeline-overview", [
    { type: "metric", config: { measure: "total" }, x: 0, y: 0, w: 4, h: 2 },
    {
      type: "metric",
      config: { measure: "total", title: "New this week", filters: { dateRange: { key: "this-week" } } },
      x: 4,
      y: 0,
      w: 4,
      h: 2,
    },
    {
      type: "metric",
      config: { measure: "total", title: "Needs attention", filters: { query: BAD_AGE_QUERY } },
      x: 8,
      y: 0,
      w: 4,
      h: 2,
    },
    {
      type: "type-mix",
      // Dimension-grouped widgets bake NO title — the module's `dynamicTitle`
      // derives one from `groupBy` and tracks regrouping.
      config: { groupBy: "stage", donut: true, percentages: true, legend: true },
      x: 0,
      y: 2,
      w: 6,
      h: 6,
    },
    { type: "series-line", config: { groupBy: "stage" }, x: 6, y: 2, w: 6, h: 6 },
    { type: "dimension-bar", config: { groupBy: "stage" }, x: 0, y: 8, w: 12, h: 5 },
  ]),
  templateBoard("team-performance", [
    { type: "dimension-bar", config: { groupBy: "assignee" }, x: 0, y: 0, w: 6, h: 6 },
    {
      type: "type-mix",
      config: { groupBy: "assignee", donut: true, percentages: true, legend: true },
      x: 6,
      y: 0,
      w: 6,
      h: 6,
    },
    {
      type: "most-active-users",
      config: { limit: 8, mode: "list", title: "Most active users" },
      x: 0,
      y: 6,
      w: 8,
      h: 4,
    },
    { type: "projects-closed", config: { includeLost: false, title: "Projects closed" }, x: 8, y: 6, w: 4, h: 4 },
  ]),
  templateBoard("projects-activity", [
    { type: "entity-list", config: { sort: "updated", limit: 25, title: "Projects" }, x: 0, y: 0, w: 8, h: 7 },
    { type: "metric", config: { measure: "total" }, x: 8, y: 0, w: 4, h: 2 },
    { type: "activity-list", config: { limit: 30, title: "Activity" }, x: 8, y: 2, w: 4, h: 5 },
  ]),
];

/**
 * A managed board's server-persisted first-read seeds: the `defaultOn` entries
 * with `widgetKey` and `managedLayoutVersion` baked into config — exactly the
 * shape the client's managed maintenance later matches and migrates by.
 */
export function managedDefaultSeeds(
  board: readonly ManagedBoardWidget[],
  layoutVersion: number,
): WidgetBoardSeed[] {
  return board
    .filter((widget) => widget.defaultOn)
    .map((widget) => ({
      type: widget.type,
      config: { ...widget.config, managedLayoutVersion: layoutVersion, widgetKey: widget.widgetKey },
      x: widget.x,
      y: widget.y,
      w: widget.w,
      h: widget.h,
    }));
}

/**
 * A dashboard template's seeds by key — throws on an unknown key so a typo in
 * a caller fails loudly at boot rather than silently seeding nothing.
 */
export function dashboardTemplateSeeds(key: DashboardTemplateKey): WidgetBoardSeed[] {
  const board = DASHBOARD_TEMPLATE_BOARDS.find((template) => template.key === key);
  if (!board) throw new Error(`Unknown dashboard template: ${key}`);
  return board.seeds;
}
